/**
 * Owner: Masa Bayu
 * File: landing-data.ts
 * Date:Time: 2026-10-23 00:05
 * Description: Model data home landing §376 — semua nombor/nama datang daripada
 *   projeksi katalog + library sebenar (tiada data statik mockup).
 *   §466 S1 — ranking gunakan medan hub nyata (tarbit/penerbit/kategori);
 *   penarafan rekaan tak boleh mengisih apa-apa lagi.
 *   §468i — rak koleksi home (pengarang/kategori) turut daripada katalog hub.
 */

import { getDemoLibraryProjection } from '../../../library/demo-library-store';
import { parseQxpPageRef } from '../../../reader/shared/reading-continuity';
import {
  MARKETPLACE_FILTERS_EMPTY,
  matchesFilters,
  type MarketplaceBook,
  type MarketplaceCategoryId,
} from '../marketplace-projection';

export interface LandingAuthor {
  readonly name: string;
  readonly bookCount: number;
}

export interface LandingCategoryCount {
  readonly categoryId: MarketplaceCategoryId;
  readonly count: number;
}

export interface LandingContinueEntry {
  readonly publicationId: string;
  readonly title: string;
  readonly percent: number;
  readonly chapter: number | null;
}

/** Terbitan terkini dahulu; buku tanpa tarikh hub kalah dan ikut tajuk. */
function byRecency(a: MarketplaceBook, b: MarketplaceBook): number {
  const da = a.publishedAt;
  const db = b.publishedAt;
  if (da !== null && db !== null && da !== db) return db.localeCompare(da);
  if (da === null && db !== null) return 1;
  if (db === null && da !== null) return -1;
  return a.title.localeCompare(b.title);
}

export function featuredBooks(books: readonly MarketplaceBook[]): readonly MarketplaceBook[] {
  return [...books].sort(byRecency).slice(0, 5);
}

export function trendingBooks(books: readonly MarketplaceBook[]): readonly MarketplaceBook[] {
  const featured = new Set(featuredBooks(books).map((book) => book.publicationId));
  return [...books]
    .filter((book) => !featured.has(book.publicationId))
    .sort(byRecency)
    .slice(0, 4);
}

/** Penerbit ialah median manifest hub; terbitan tanpa penulis tidak dikira. */
export function topAuthors(books: readonly MarketplaceBook[], limit = 5): readonly LandingAuthor[] {
  const byAuthor = new Map<string, LandingAuthor>();
  for (const book of books) {
    if (book.author === null) continue;
    const current = byAuthor.get(book.author);
    byAuthor.set(book.author, {
      name: book.author,
      bookCount: (current?.bookCount ?? 0) + 1,
    });
  }
  return [...byAuthor.values()]
    .sort((a, b) => b.bookCount - a.bookCount || a.name.localeCompare(b.name))
    .slice(0, limit);
}

export function popularCategories(
  books: readonly MarketplaceBook[],
): readonly LandingCategoryCount[] {
  const counts = new Map<MarketplaceCategoryId, number>();
  for (const book of books) {
    if (book.categoryId === null) continue;
    counts.set(book.categoryId, (counts.get(book.categoryId) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([categoryId, count]) => ({ categoryId, count }))
    .sort((a, b) => b.count - a.count || a.categoryId.localeCompare(b.categoryId));
}

export function continueReadingEntries(): readonly LandingContinueEntry[] {
  const projection = getDemoLibraryProjection();
  return projection.continueReading
    .map((item) => ({
      publicationId: item.publicationId,
      title: item.title,
      percent: item.progress.percentComplete,
      chapter: parseQxpPageRef(item.progress.lastPositionRef),
    }))
    .sort((a, b) => b.percent - a.percent)
    .slice(0, 3);
}

export function wishlistedIds(): readonly string[] {
  return getDemoLibraryProjection().wishlist.map((item) => item.publicationId);
}

/** §468i — rak koleksi home: satu-satunya sumber ialah katalog hub (§466 S1).
 *  Tiada senarai buku rekaan; rak kosong tidak dipaparkan.
 *  §468n — rak Darwishsyah + Kanak-Kanak dibuang atas arahan Owner. */
export type LandingCollectionId = 'masaBayu' | 'alamtologi';

export interface LandingCollection {
  readonly id: LandingCollectionId;
  readonly books: readonly MarketplaceBook[];
  readonly seeAllPath: string;
}

type CollectionRule =
  | {
      readonly id: LandingCollectionId;
      readonly kind: 'author';
      readonly value: string;
      readonly seeAllPath: string;
    }
  | {
      readonly id: LandingCollectionId;
      readonly kind: 'category';
      readonly value: MarketplaceCategoryId;
      readonly seeAllPath: string;
    };

/** Nama pengarang dipadan tepat (trim + huruf besar/kecil diabaikan);
 *  kategori dipadan melalui penuras SSOT §405 (primer · sub · sekunder). */
const COLLECTION_RULES: readonly CollectionRule[] = Object.freeze([
  {
    id: 'masaBayu',
    kind: 'author',
    value: 'masa bayu',
    seeAllPath: `/all-books?q=${encodeURIComponent('Masa Bayu')}`,
  },
  {
    id: 'alamtologi',
    kind: 'category',
    value: 'alamtologi',
    seeAllPath: '/all-books?category=alamtologi',
  },
]);

const COLLECTION_LIMIT = 5;

function ruleMatches(book: MarketplaceBook, rule: CollectionRule): boolean {
  if (rule.kind === 'author') {
    return (book.author?.trim().toLowerCase() ?? '') === rule.value;
  }
  return matchesFilters(book, { ...MARKETPLACE_FILTERS_EMPTY, category: rule.value });
}

/** Rak dalam tertib arahan Owner; setiap rak dipangkas kepada `COLLECTION_LIMIT`
 *  buku terkini supaya tinggi rak sebaris dengan Featured Books. */
export function bookCollections(books: readonly MarketplaceBook[]): readonly LandingCollection[] {
  return COLLECTION_RULES.map((rule) => ({
    id: rule.id,
    books: books
      .filter((book) => ruleMatches(book, rule))
      .sort(byRecency)
      .slice(0, COLLECTION_LIMIT),
    seeAllPath: rule.seeAllPath,
  }));
}
